// eslint-disable-next-line import/no-extraneous-dependencies
import React, {
    ReactElement, ReactNode, HTMLAttributes,
    JSX,
} from "react";
// eslint-disable-next-line import/no-extraneous-dependencies
import {
    SortableContext, arrayMove, horizontalListSortingStrategy,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
// eslint-disable-next-line import/no-extraneous-dependencies
import {
    DndContext,
    DragStartEvent,
    PointerSensor, closestCenter, useDroppable, useSensor, useSensors,
} from "@dnd-kit/core";
// eslint-disable-next-line import/no-extraneous-dependencies
import { restrictToHorizontalAxis, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import SortableItem from "./SortableItem";
import Listener from "./SortableItemListener";
import SortableItemDroppable from "./SortableItemDroppable";
import SortableResizableItem from "./SortableResizableItem";
import { useCombinedRef } from "../../useOrCreateRef";

type AsProp = keyof JSX.IntrinsicElements;

export interface SortableListProps<T extends {id: string}>
    extends Omit<HTMLAttributes<HTMLElement>, "onDragStart" | "onDragEnd"> {
    id: string;
    children?: ReactNode;
    items: T[];
    direction?: "horizontal" | "vertical";
    nodeType?: AsProp;
    onReorder?: (items: T[], reordered: T) => void;
    onDragStart?: (event: DragStartEvent) => void;
    onDragEnd?: (event: DragStartEvent) => void;
}

const SortableItemList = React.forwardRef(<T extends {id: string}>(
    args: SortableListProps<T>,
    ref: React.ForwardedRef<HTMLElement>,
): ReactElement => {
    const {
        id,
        children,
        items,
        direction,
        onReorder,
        nodeType,
        onDragStart,
        onDragEnd,
        ...rest
    } = args;
    const { setNodeRef: setDraggableRef } = useDroppable({
        id,
    });

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
    );

    const reference = useCombinedRef<HTMLElement | null>([ref, setDraggableRef]);

    const Element = (nodeType ?? "div") as React.ElementType;

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[direction === "vertical" ? restrictToVerticalAxis : restrictToHorizontalAxis]}
            onDragStart={onDragStart}
            onDragEnd={event => {
                if (!onReorder) {
                    return;
                }
                const oldIndex = items.findIndex(row => row.id === event.active.id.toString());
                const movedItem = items[oldIndex];
                const newIndex = items.findIndex(row => row.id === event.over?.id.toString());
                if (newIndex < 0) {
                    return;
                }
                const copy = arrayMove(items, oldIndex, newIndex);
                onReorder(copy, movedItem);

                onDragEnd?.(event);
            }}
        >

            <SortableContext
                items={items}
                disabled={!onReorder}
                strategy={direction === "horizontal" ? horizontalListSortingStrategy : verticalListSortingStrategy}
            >
                <Element
                    id={id}
                    ref={reference}
                    {...rest}
                >
                    {children}
                </Element>

            </SortableContext>
        </DndContext>
    );
});

SortableItemList.defaultProps = {
    direction: "vertical",
    nodeType: "div",
};

type SortableListTypeWithTypes = (<
    T extends {id: string},
> (
    props: SortableListProps<T> & { ref?: React.ForwardedRef<HTMLUListElement> }
) => ReturnType<typeof SortableItemList>) & {
    Item: typeof SortableItem;
    Listener: typeof Listener;
    Droppable: typeof SortableItemDroppable;
    SortableResizableItem: typeof SortableResizableItem;
}

const SortableList = SortableItemList as unknown as SortableListTypeWithTypes;
SortableList.Item = SortableItem;
SortableList.Listener = Listener;
SortableList.Droppable = SortableItemDroppable;
SortableList.SortableResizableItem = SortableResizableItem;

export default SortableList;
